import ClientSideTable from '@/components/Table/ClientSideTable'
import { NumberInputCell, PriceCell, RawTextCell } from '@/components/Table/components/Cell'
import DatePickerCell from '@/components/Table/components/Cell/DatePickerCell'
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import EditableTable from '@/components/Table/EditableTable'
import { formatOffer } from '@/features/offers/helpers/formatOffer'
import useOffer from '@/features/offers/hooks/useOffer'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { prepareAndValidateSourcingPostData } from '@/features/offers/offer-detail/helper/sourcing.helper'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import useVendor from '@/features/vendors/useVendor'
import { showModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { getExchangeRateToHKD, useExchangeRate } from '@/utils/exchangeRate.helper'
import { numberWithCommas } from '@/utils/stringHelper'
import { Button, Flex, Input, Select, Spacer, Text } from '@chakra-ui/react'
import {
  COST_BUDGET_HEADER_MAPPING,
  getQuotationDetailTableColumnIds,
  PRStatus,
  SourcingPostType,
} from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { usePRAccess } from '../../../PRAccessProvider'
import { calculateRequestedCost } from '../../../utils/pr.helper'
import { isNullish } from '@/utils/helper'

const PRQuotationDetailSection = ({
  costBudgetIndex,
  vendorSelectionIndex,
  handleAddPRSubmission,
  handleUpdatePRSubmission,
}) => {
  const { user } = useSelector(authSelector)
  const { adminMode } = useSelector(selectOfferDetail)
  const { canEditPRSubmission } = usePRAccess()
  const dispatch = useDispatch()

  const { setValue, getValues } = useFormContext()

  const { useVendorOptions } = useVendor()
  const { useUpdateOffer } = useOffer()
  const { data: vendorOptions } = useVendorOptions({ valueKey: '_id' })
  const { useFetchAllUsers } = useUser()
  const updateOffer = useUpdateOffer()
  const { data: usersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  const columnHelper = createColumnHelper()

  const PRSubmissions =
    useWatch({
      name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions`,
    }) ?? []
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })
  const watchedVendorSelections = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections` })
  const vendorSelections = useMemo(() => watchedVendorSelections ?? [], [watchedVendorSelections])
  const watchedVendorDetails = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors` })
  const vendorDetails = useMemo(() => watchedVendorDetails ?? [], [watchedVendorDetails])
  const discountAmount =
    useWatch({
      name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.discountAmount`,
    }) ?? 0
  const currency =
    useWatch({
      name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.currency`,
    }) ?? 'HKD'

  const watchedEngineers = useWatch({ name: 'engineers' })
  const engineers = useMemo(() => watchedEngineers ?? [], [watchedEngineers])
  const bookingProjectManager = useWatch({ name: 'booking.projectManager' })
  const sales = useWatch({ name: 'createdBy' })
  const { data: exchangeRateMap } = useExchangeRate()

  const [requestor, setRequestor] = useState('')

  //Translated
  const canEditPR = canEditPRSubmission(costBudgetIndex)
  const allPRCompleted = PRSubmissions.every(
    (submission) => submission.status === PRStatus.APPROVED || submission.status === PRStatus.ARCHIVE
  )
  const exchangeRate = getExchangeRateToHKD(currency, exchangeRateMap)

  const displayingQuotationDetails =
    vendorSelectionIndex >= 0 ? vendorSelections[vendorSelectionIndex]?.quotationDetails : []

  const vendorSelectionOptions = vendorSelections.map((selection, index) => {
    const vendorOption = vendorOptions.find((opt) => opt.value === selection.vendor)
    return {
      label: vendorOption?.label ?? '(Missing Label)',
      value: index,
    }
  })

  const requestorOptions = useMemo(() => {
    if (!Array.isArray(usersResponse?.data)) {
      return []
    }

    const allowedRequestorIds = new Set(
      [...engineers, bookingProjectManager, sales]
        .map((userDetail) => userDetail?._id ?? userDetail)
        .filter(Boolean)
        .map((id) => String(id))
    )

    return usersResponse.data
      .filter((userDetail) => allowedRequestorIds.has(String(userDetail._id)))
      .map((userDetail) => ({
        label: userDetail.fullName,
        value: userDetail._id,
      }))
  }, [usersResponse?.data, engineers, bookingProjectManager, sales])

  useEffect(() => {
    if (requestor || !user?._id) {
      return
    }

    const isCurrentUserAllowed = requestorOptions.some((option) => option.value === user._id)
    if (isCurrentUserAllowed) {
      setRequestor(user._id)
    }
  }, [requestor, user?._id, requestorOptions])

  const handleUpdateTableData = (rowIndex, columnId, newValue) => {
    setValue(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.quotationDetails.${rowIndex}.${columnId}`,
      newValue,
      {
        shouldDirty: true,
      }
    )
  }
  const handleAutoFillQty = () => {
    displayingQuotationDetails.forEach((row, rowIndex) => {
      const totalRequestedQuantity = row?.totalRequestedQuantity ?? 0
      const quantity = row?.quantity ?? 0

      const autoFilledQuantity = quantity > totalRequestedQuantity ? quantity - totalRequestedQuantity : 0
      setValue(
        `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.quotationDetails.${rowIndex}.newRequestedQuantity`,
        autoFilledQuantity,
        { shouldDirty: true }
      )
    })
  }

  const handleRequestDetail = async () => {
    try {
      const offerId = getValues('_id')

      const costBudgetDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail`)
      const costBudgetId = costBudgetDetail._id
      const offer = getValues()
      const {
        procurementOfficer,
        tenderDocumentIndex,
        vendorDetails: sourcingVendorDetails,
        technicalUsers,
        ccList,
      } = prepareAndValidateSourcingPostData({
        offer,
        costBudgetDetail,
        costBudgetIndex,
      })

      const formattedOffer = formatOffer(offer)

      // Get selected quotation file from the currently selected vendor selection
      const currentVendorSelection = vendorSelections[vendorSelectionIndex]
      const selectedVendorId = currentVendorSelection?.vendor
      const selectedUploadHistoryIndex = currentVendorSelection?.uploadHistoryIndex

      const selectedVendor = vendorDetails.find(
        (detail) => String(detail?.vendor ?? '') === String(selectedVendorId ?? '')
      )
      let quotationFileId = ''

      if (selectedVendor?.history && selectedVendor.history.length > 0) {
        const selectedQuotation =
          Number.isInteger(selectedUploadHistoryIndex) && selectedUploadHistoryIndex >= 0
            ? selectedVendor.history[selectedUploadHistoryIndex]
            : null

        if (selectedQuotation?.files && selectedQuotation.files.length > 0) {
          quotationFileId = selectedQuotation.files[0]?._id || ''
        }
      }

      const initialExtraDocuments = quotationFileId ? [{ _id: quotationFileId }] : []

      await updateOffer.mutateAsync({
        id: offerId,
        data: formattedOffer,
      })

      dispatch(
        showModal({
          modalType: MODAL_TYPES.RFQ,
          modalProps: {
            title: 'Request Detail Table',
            offerId: offerId,
            costBudgetId: costBudgetId,
            tenderDocumentIndex: tenderDocumentIndex,
            vendorDetails: sourcingVendorDetails,
            technicalUsers: technicalUsers,
            procurementOfficer: procurementOfficer,
            ccList: ccList,
            requestType: SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
            canChangePostType: false,
            initialRemark: 'Please fill the table…',
            initialExtraDocuments: initialExtraDocuments,
          },
        })
      )
    } catch (error) {
      toast.error('Error opening request detail modal')
      console.error(error)
    }
  }

  const handleGeneratePR = () => {
    if (!requestor) {
      toast.error('Requestor cannot be empty')
      return
    }

    const selectedQuotationDetails = vendorSelections[vendorSelectionIndex]?.quotationDetails

    const lumpSumDiscount = vendorSelections[vendorSelectionIndex]?.discountAmount ?? 0

    const quotationTotalPrice = selectedQuotationDetails.reduce((acc, row) => {
      const quantity = row?.quantity ?? 0
      const unitRate = row?.unitRate ?? 0
      const discount = row?.discount ?? 0
      const amount = quantity * unitRate * (1 - discount / 100)
      return acc + amount
    }, 0)

    const requestedDetails = selectedQuotationDetails
      .map((row) => {
        //Calculate how to distribute the overall discount amount to each row based on the row's total price proportion

        const quantity = row?.quantity ?? 0
        const unitRate = row?.unitRate ?? 0
        const discount = row?.discount ?? 0
        const amount = quantity * unitRate * (1 - discount / 100)

        const lumpSumDiscountProportion = (amount / quotationTotalPrice) * lumpSumDiscount

        const lumpSumDiscountPerItem = lumpSumDiscountProportion / quantity

        const requestedTotalPrice = calculateRequestedCost({
          row,
          lumpSumDiscountAmount: lumpSumDiscount,
          quotationTotalPrice,
        })

        return {
          ...row,
          modelNumber: row.modelNumber,
          productDescription: row.productDescription,
          quantity: row.newRequestedQuantity,
          unit: row.unit,
          unitRate: row.unitRate,
          totalPrice: requestedTotalPrice,
          lumpSumDiscount: lumpSumDiscountPerItem,
        }
      })
      .filter((row) => row.quantity > 0)

    if (requestedDetails.length === 0) {
      toast.error('Please fill at least 1 row')
      return
    }

    const requestorDetail = usersResponse?.data?.find((userDetail) => userDetail._id === requestor)
    if (!requestorDetail) {
      toast.error('Please select a valid requestor')
      return
    }

    handleAddPRSubmission(requestedDetails, requestor)
  }

  const vendorQuotationDetailColumns = useMemo(() => {
    const tableColumnIds = getQuotationDetailTableColumnIds({
      costBudgetType,
      isTemplate: false,
      canRemove: true,
    })

    return tableColumnIds
      .filter((columnId) => !['action', 'ECCNDetail'].includes(columnId))
      .map((columnId) => {
        const accessorId =
          columnId === 'scopeOfWorkSelect'
            ? 'scopeOfWork'
            : ['unitSelect', 'unitMaintenance'].includes(columnId)
              ? 'unit'
              : columnId

        const header = COST_BUDGET_HEADER_MAPPING[columnId] ?? columnId

        const formattedHeader = ['unitRate', 'totalPrice'].includes(columnId) ? `${header} (${currency})` : header

        return columnHelper.accessor(accessorId, {
          id: columnId,
          header: () => <span>{formattedHeader}</span>,
          cell: (cell) => {
            switch (columnId) {
              case 'unitRate':
              case 'totalPrice':
                return <PriceCell {...cell} />
              case 'startDate':
              case 'endDate':
                return <DatePickerCell {...cell} extraDisable={true} />
              default:
                return <RawTextCell {...cell} />
            }
          },
          meta: {
            filterVariant: FilterType.SIMPLE_TEXT,
          },
        })
      })
  }, [columnHelper, costBudgetType])

  const columnDef = useMemo(
    () => [
      columnHelper.group({
        header: ' ',
        columns: [
          columnHelper.display({
            id: 'vendorHistoryIndex',
            size: 10,
            header: () => <span>ref</span>,
            cell: ({ row: { index } }) => <TooltipCell text={index + 1} />,
          }),
        ],
      }),

      columnHelper.group({
        header: 'Vendor Quotation Detail',
        footer: 'Total',
        columns: vendorQuotationDetailColumns,
      }),
      columnHelper.group({
        header: 'Total PR Requested',
        footer: 'Total',
        columns: [
          columnHelper.accessor('totalRequestedQuantity', {
            header: () => <span>Qty</span>,
            cell: (cell) =>
              adminMode ? <NumberInputCell {...cell} forceEnable decimalScale={2} /> : <RawTextCell {...cell} />,
            meta: {
              filterVariant: FilterType.SIMPLE_TEXT,
            },
          }),
          columnHelper.accessor('totalRequestedCost', {
            header: () => <span>Total Cost (HKD)</span>,
            cell: (cell) => {
              if (adminMode) {
                return <NumberInputCell {...cell} forceEnable decimalScale={2} />
              }

              const price = cell.getValue()

              const priceHKD = price * exchangeRate

              if (isNullish(price) || price === '') {
                return <>(-)</>
              }

              return <TooltipCell text={`$${numberWithCommas(priceHKD)}`} />
            },
            meta: {
              filterVariant: FilterType.SIMPLE_TEXT,
            },
          }),
        ],
      }),
      columnHelper.group({
        header: 'New PR Requested',
        footer: 'Total',
        columns: [
          columnHelper.accessor('newRequestedQuantity', {
            header: () => <span>Qty</span>,
            enableColumnFilter: false,
            cell: ({ table, row: { index: rowIndex, original }, column: { id: columnId }, getValue }) => {
              const tableValue = getValue()
              // eslint-disable-next-line react-hooks/rules-of-hooks -- TanStack renders cells as components
              const [localValue, setLocalValue] = useState(tableValue)
              const isDisabled = table.options.meta?.disabled
              const totalRequestedQuantity = original?.totalRequestedQuantity
              const totalQuantity = original?.quantity

              // eslint-disable-next-line react-hooks/rules-of-hooks -- TanStack renders cells as components
              useEffect(() => {
                if (tableValue) {
                  setLocalValue(tableValue)
                }
              }, [tableValue])

              return (
                <Input
                  as={NumberFormat}
                  allowNegative={false}
                  value={localValue}
                  decimalScale={2}
                  onValueChange={({ value, floatValue }) => {
                    setLocalValue(floatValue)
                  }}
                  onBlur={() => {
                    table.options.meta?.updateData(rowIndex, columnId, localValue)
                  }}
                  placeholder={'Quantity'}
                  disabled={isDisabled}
                />
              )
            },
          }),
          columnHelper.display({
            id: 'newRequestedCost',
            header: () => <span>Total Cost (HKD)</span>,
            cell: ({ row: { original } }) => {
              const quotationTotalPrice = displayingQuotationDetails.reduce((acc, row) => {
                const quantity = Number(row?.quantity ?? 0)
                const unitRate = Number(row?.unitRate ?? 0)
                const discount = Number(row?.discount ?? 0)
                const amount = quantity * unitRate * (1 - discount / 100)

                return acc + amount
              }, 0)

              const newRequestedCost = calculateRequestedCost({
                row: original,
                lumpSumDiscountAmount: discountAmount,
                quotationTotalPrice,
              })
              const newRequestedCostHKD = newRequestedCost * exchangeRate

              return <span>${numberWithCommas(newRequestedCostHKD)}</span>
            },
          }),
        ],
      }),
    ],
    [columnHelper, discountAmount, displayingQuotationDetails, exchangeRate, vendorQuotationDetailColumns, adminMode]
  )

  return (
    <Flex gap="2" direction={'column'} py="2">
      <Flex justifyContent={'flex-end'} gap="2">
        <Button colorScheme="teal" onClick={handleAutoFillQty} isDisabled={!canEditPR}>
          Auto Fill Qty
        </Button>
        <Button colorScheme="blue" onClick={handleRequestDetail} isDisabled={!canEditPR}>
          Request Detail
        </Button>
      </Flex>

      <ClientSideTable
        textAlign={'center'}
        columnDef={columnDef}
        data={displayingQuotationDetails}
        tableWidth="100%"
        disabled={!canEditPR}
        updateData={handleUpdateTableData}
      />

      <Flex direction={'row'} gap="3" justifyContent={'end'} py="2">
        <Flex gap="4" align="center">
          <Text fontWeight="semibold" minW="200px" textAlign="right">
            Lump Sum Discount ({currency}):
          </Text>
          <Text minW="140px" textAlign="right">
            ${numberWithCommas(discountAmount)}
          </Text>
        </Flex>
        <Spacer />

        <Flex gap="2" align="center" minW="280px">
          <Text minW="80px" textAlign="right">
            Requestor:
          </Text>
          <Select
            placeholder="Select requestor"
            value={requestor}
            onChange={(event) => setRequestor(event.target.value)}
            isDisabled={!canEditPR}
          >
            {requestorOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Flex>
        <Button colorScheme="green" onClick={handleGeneratePR} isDisabled={!canEditPR || !requestor}>
          Generate PR
        </Button>
      </Flex>
    </Flex>
  )
}

export default PRQuotationDetailSection
